import { useState, useEffect, useCallback, useRef } from "react";
import { collection, getDoc, getDocs, doc } from "firebase/firestore";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";
import { db } from "../../firebase";
import Input from "../Input";
import { getPartnerLinePrice } from "../../utility/partnerPricing";
import { createPartnerCheckout, resumePartnerCheckout } from "../../utility/partnerCheckout";
import PartnerTickets from "./PartnerTickets";
import "./partnerCheckout.scss";
import TicketItem from "../TicketItem";
import LoadingSpinner from "../LoadingSpinner";
import { createPartnerParticipant } from "../../utility";
import { downloadPDF, sendEmail } from "../../utility/createDownloadTicket";
import ModalComponent from "../modal/Modal";
import { Asterisk } from "../../images/Asterisk";
import CsvForm from "./ExcelForm";
import ProgressBar from "../progressBar/ProgressBar";
import { emailValidation } from "../../helper/regexValidations";
import {
  buildOptionSelectionLabel,
  buildVariantKey,
  getOptionPriceDelta,
  getRemainingOptionStock,
} from "../../utility/ticketOptions";

const EventDescPartner = (props) => {
  const { t } = useTranslation(["eventdesc", "ticketitem"]);
  const eventId = props.event?.id ?? "";

  const [tickets, setTickets] = useState([]);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [currentPromoCode, setCurrentPromoCode] = useState(null);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [payer, setPayer] = useState({});
  const checkoutLock = useRef(false);
  const paymentRequest = useRef(null);
  const [participants, setParticipants] = useState([]);
  const [isCsvCreation, setIsCsvCreation] = useState(false);
  const [showTicketHistory, setShowTicketHistory] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [failModalOpen, setFailModalOpen] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");
  const [openOptionsTicketId, setOpenOptionsTicketId] = useState(null);
  const [optionDraftByTicket, setOptionDraftByTicket] = useState({});

  const alert = useAlert();

  const inputChangeHandler = (label, value) =>
    setFormValues((prev) => ({ ...prev, [label]: value }));

  const totalTickets = (tickets) =>
    tickets.reduce((total, ticket) => total + Number(ticket.number || 0), 0);

  const getTicketAvailableQuantity = (ticket) => {
    const ownReservation = currentPromoCode?.reserveTicketCapacity
      ? Number(currentPromoCode.reservedTicketQuantities?.[ticket?.id] || 0)
      : 0;
    return Math.max(
      0,
      Number(ticket?.numAvailable || 0) -
        Number(ticket?.amountSold || 0) -
        Number(ticket?.reservedPromoQuantity || 0) -
        Number(ticket?.pendingCheckoutQuantity || 0) +
        ownReservation - Number(currentPromoCode?.pendingPartnerReservations?.[ticket?.id] || 0),
    );
  };

  // Partner links remain valid independently of the public sale status and
  // sale period. Availability is still enforced by the partner code, quota
  // and ticket/option inventory checks on the server.
  const usedPartnerQuota = Number(currentPromoCode?.currentUsage || 0) + Number(currentPromoCode?.pendingPartnerUsage || 0);
  const showForm =
    currentPromoCode?.maxUsage === 0 ||
    usedPartnerQuota < currentPromoCode?.maxUsage;
  useEffect(() => {
    if (currentPromoCode && !showForm) setShowTicketHistory(true);
  }, [currentPromoCode, showForm]);
  const pricedTickets = selectedTickets.map((line) => getPartnerLinePrice(tickets.find((ticket) => ticket.id === line.ticketId), line, currentPromoCode));
  const participantPrice = (participant) => getPartnerLinePrice(tickets.find((ticket) => ticket.id === participant.ticketId), { ticketId: participant.ticketId, number: Number(participant.quantity || 0), optionSelections: participant.optionSelections || [] }, currentPromoCode).sum;
  const totalAmount = isCsvCreation
    ? participants.filter((participant) => !participant.participantData).reduce((sum, participant) => sum + participantPrice(participant), 0)
    : pricedTickets.reduce((sum, line) => sum + line.sum, 0);
  const loadTicketsPromoCode = useCallback(
    async (signal) => {
      setLoadingTickets(true);
      const ticketsRef = collection(db, "Events", eventId, "Tickets");
      const promoCodeRef = doc(db, "Events", eventId, "PromoCodes", props.partnerId);

      let loadedTickets = [];
      let promoCode = null;

      try {
        try {
          const previous = await resumePartnerCheckout({ eventId, partnerId: props.partnerId });
          if (["failed", "expired", "refunded", "refund_failed"].includes(previous?.status)) paymentRequest.current = null;
        } catch (error) { console.error("Partner payment refresh failed", error.message); }
        const ticketsSnapshot = await getDocs(ticketsRef);
        const promoCodeSnapshot = await getDoc(promoCodeRef);

        loadedTickets = ticketsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          promoPrice: 0,
        }));

        if (promoCodeSnapshot.exists()) {
          promoCode = {
            id: promoCodeSnapshot.id,
            ...promoCodeSnapshot.data(),
          };
        }
      } catch (error) {
        if (!signal || !signal?.aborted) {
          console.error(t("fetch_tickets_failed") + ": " + error.message);
          setLoadingTickets(false);
        }
        return;
      }

      const ticketsToShow = loadedTickets.filter((ticket) =>
        promoCode?.ticketDiscounts?.some((td) => td.ticketId === ticket.id),
      );

      if (!signal || !signal?.aborted) {
        setTickets(ticketsToShow);
        setCurrentPromoCode(promoCode);
        setLoadingTickets(false);
      }
    },
    [eventId, props.partnerId, t],
  );

  useEffect(() => {
    const abortController = new AbortController();
    const refresh = () => {
      if (document.visibilityState === "visible") loadTicketsPromoCode(abortController.signal);
    };
    const restore = (event) => { if (event.persisted) refresh(); };

    if (props.event) {
      loadTicketsPromoCode(abortController.signal);
    }

    window.addEventListener("pageshow", restore);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      abortController.abort();
      window.removeEventListener("pageshow", restore);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [props.event, loadTicketsPromoCode]);

  const checkoutHandler = async (e, deliveryMode = "email") => {
    e.preventDefault();
    if (checkoutLock.current) return;
    checkoutLock.current = true;
    setIsLoading(true);

    try {
      const ticketsNb = isCsvCreation
        ? participants
            .filter((participant) => !participant.participantData)
            .reduce((total, participant) => total + Number(participant.quantity || 0), 0)
        : totalTickets(selectedTickets);

      if (ticketsNb === 0) {
        alert.show(t("select_at_least_one_ticket"), { type: "error" });
        return;
      }
      if (
        currentPromoCode.maxUsage !== 0 &&
        ticketsNb + usedPartnerQuota > currentPromoCode.maxUsage
      ) {
        alert.show(t("max_tickets_reached", { count: currentPromoCode.maxUsage }), {
          type: "error",
        });
        return;
      }

      if (totalAmount > 0) {
        const paymentMethod = deliveryMode === "card" ? "card" : "bancontact";
        let beneficiaries;
        if (isCsvCreation) {
          const validation = validateBulkParticipants();
          if (!validation.isValid) { setParticipants(validation.participants); return; }
          beneficiaries = validation.participants.filter((participant) => !participant.participantData).map((participant) => ({
            details: [{ name: "Firstname", value: participant.firstname }, { name: "Name", value: participant.lastname }, { name: "Email", value: participant.email }],
            tickets: [{ ticketId: participant.ticketId, number: Number(participant.quantity), optionSelections: participant.optionSelections || [] }],
          }));
        } else {
          beneficiaries = [{ details: [{ name: "Firstname", value: formValues.firstname || "" }, { name: "Name", value: formValues.lastname || "" }, { name: "Email", value: formValues.email || "" }], tickets: selectedTickets }];
        }
        const buyer = isCsvCreation ? payer : { firstname: formValues.firstname, lastname: formValues.lastname, email: formValues.email };
        if (!buyer.firstname?.trim() || !buyer.lastname?.trim() || !emailValidation(buyer.email || "")) {
          alert.show(t("fill_all_form_fields"), { type: "error" }); return;
        }
        const payload = { eventId, partnerId: props.partnerId, paymentMethod, payer: buyer, beneficiaries, message };
        const fingerprint = JSON.stringify(payload);
        if (paymentRequest.current?.fingerprint !== fingerprint) paymentRequest.current = { fingerprint, requestId: crypto.randomUUID() };
        await createPartnerCheckout({ ...payload, requestId: paymentRequest.current.requestId });
        return;
      }

      const checkoutCompleted = isCsvCreation
        ? await handleCsvCheckout(deliveryMode)
        : await handleManualCheckout(ticketsNb, deliveryMode);

      if (!checkoutCompleted) return;

      if (deliveryMode === "email") {
        setSelectedTickets([]);
      }
      await loadTicketsPromoCode();
    } catch (err) {
      alert.show(err?.message || t("registration_failed"), { type: "error" });
      console.error(t("failed_to_save_participant"), err);
    } finally {
      setProgress(0);
      checkoutLock.current = false;
      setIsLoading(false);
    }
  };

  const handleCsvCheckout = async (deliveryMode = "email") => {
    const validation = validateBulkParticipants();
    if (!validation.isValid) {
      setParticipants(validation.participants);
      return false;
    }

    let successful = 0;
    let participantsFailed = [];

    for (const participant of validation.participants) {
      const ticketIndex = tickets.findIndex((t) => t.id === participant.ticketId);

      if (ticketIndex !== -1) {
        try {
          await createParticipantAndDeliverTicket(
            [
              {
                ticketId: participant.ticketId,
                number: Number(participant.quantity),
                sum: 0,
                name: tickets[ticketIndex].name,
                optionSelections: participant.optionSelections || [],
              },
            ],
            [
              { name: "Firstname", value: participant.firstname },
              { name: "Name", value: participant.lastname },
              { name: "Email", value: participant.email },
            ],
            participant.participantData,
            deliveryMode,
          );
          successful++;
        } catch (err) {
          console.error(`Error for ${participant.firstname}:`, err);
          participantsFailed.push({
            ...participant,
            participantData: err?.participantData,
            error: err?.message || t("registration_failed"),
          });
        }
      } else {
        participantsFailed.push({ ...participant, error: t("invalid_ticket_name", { ticketName: participant.ticketName }) });
      }

      setProgress(
        Math.round(((successful + participantsFailed.length) / participants.length) * 100),
      );
    }

    if (participantsFailed.length > 0) {
      setParticipants(participantsFailed);
      setFailureMessage(participantsFailed.find((participant) => participant.error)?.error || "");
      setFailModalOpen(true);
      alert.show(
        t("registration_partial_failure", {
          success: successful,
          failed: participantsFailed.length,
        }),
        {
          type: "warning",
        },
      );
    } else {
      setParticipants([]);
      alert.show(t("registration_successful"), { type: "success" });
    }

    return successful > 0;
  };

  const validateBulkParticipants = () => {
    const ticketCountById = {};
    const optionCountByKey = {};
    let hasError = false;
    const validatedParticipants = participants.map((participant) => {
      if (participant.participantData) return { ...participant, error: "" };

      let error = participant.optionsParseError ? t("invalidOptionsFormat") : "";
      if (!participant.firstname?.trim() || !participant.lastname?.trim() || !participant.email?.trim()) {
        error = t("some_fields_empty");
      } else if (!emailValidation(participant.email)) {
        error = t("invalid_email_format");
      } else if (!tickets.some((ticket) => ticket.id === participant.ticketId)) {
        error = t("select_ticket");
      } else if (!Number.isInteger(Number(participant.quantity)) || Number(participant.quantity) <= 0) {
        error = t("invalid_quantity");
      }

      const ticket = tickets.find((item) => item.id === participant.ticketId);
      if (!error && ticket) {
        const selectionsByGroup = new Map();
        for (const selection of participant.optionSelections || []) {
          if (!selectionsByGroup.has(selection.groupId)) selectionsByGroup.set(selection.groupId, []);
          selectionsByGroup.get(selection.groupId).push(selection.valueId);
        }

        for (const group of ticket.options || []) {
          const selectedValueIds = selectionsByGroup.get(group.id) || [];
          const validValues = (group.values || []).map((value) => value.id);
          const hasUnknownValue = selectedValueIds.some((valueId) => !validValues.includes(valueId));
          const maxSelections =
            group.selectionType === "multi"
              ? Number(group.maxSelect || 0) || Number.POSITIVE_INFINITY
              : 1;
          if (
            hasUnknownValue ||
            new Set(selectedValueIds).size !== selectedValueIds.length ||
            (group.required && selectedValueIds.length === 0) ||
            selectedValueIds.length > maxSelections
          ) {
            error = t("invalidTicketOptions");
            break;
          }
        }
        if (
          !error &&
          [...selectionsByGroup.keys()].some(
            (groupId) => !(ticket.options || []).some((group) => group.id === groupId),
          )
        ) {
          error = t("invalidTicketOptions");
        }
      }

      if (error) {
        hasError = true;
        return { ...participant, error };
      }

      ticketCountById[participant.ticketId] =
        (ticketCountById[participant.ticketId] || 0) + Number(participant.quantity);
      for (const selection of participant.optionSelections || []) {
        const optionKey = `${participant.ticketId}::${selection.groupId}::${selection.valueId}`;
        optionCountByKey[optionKey] =
          (optionCountByKey[optionKey] || 0) + Number(participant.quantity);
      }
      return { ...participant, error: "" };
    });

    const requestedTicketCount = Object.values(ticketCountById).reduce(
      (total, count) => total + count,
      0,
    );
    let availabilityError = "";
    for (const [ticketId, count] of Object.entries(ticketCountById)) {
      const ticket = tickets.find((item) => item.id === ticketId);
      const available = getTicketAvailableQuantity(ticket);
      if (count > available) {
        availabilityError = t("ticket_count_exceeds", { ticketCount: count, ticketId, ticketAvailable: available });
        break;
      }
    }
    if (!availabilityError) {
      for (const [optionKey, count] of Object.entries(optionCountByKey)) {
        const [ticketId, groupId, valueId] = optionKey.split("::");
        const ticket = tickets.find((item) => item.id === ticketId);
        const available = getRemainingOptionStock(ticket, groupId, valueId, []);
        if (available !== null && count > available) {
          availabilityError = t("optionOutOfStock", {
            value: ticket?.options
              ?.find((group) => group.id === groupId)
              ?.values?.find((value) => value.id === valueId)?.label || "",
          });
          break;
        }
      }
    }
    if (
      !availabilityError &&
      currentPromoCode?.maxUsage &&
      requestedTicketCount + Number(usedPartnerQuota || 0) > currentPromoCode.maxUsage
    ) {
      availabilityError = t("total_tickets_exceeds", {
        totalTicketNb: requestedTicketCount,
        currentUsage: usedPartnerQuota || 0,
        excess: requestedTicketCount + Number(usedPartnerQuota || 0) - currentPromoCode.maxUsage,
      });
    }
    if (availabilityError) {
      hasError = true;
      return {
        isValid: false,
        participants: validatedParticipants.map((participant) =>
          participant.participantData ? participant : { ...participant, error: availabilityError },
        ),
      };
    }

    return { isValid: !hasError, participants: validatedParticipants };
  };

  const handleManualCheckout = async (numTickets, deliveryMode = "email") => {
    if (!(formValues.email && formValues.firstname && formValues.lastname)) {
      alert.show(t("fill_all_form_fields"), { type: "error" });
      return false;
    }

    if (!emailValidation(formValues.email)) {
      alert.show(t("invalid_email"), { type: "error" });
      return false;
    }

    try {
      await createParticipantAndDeliverTicket(
        selectedTickets,
        [
          { name: "Firstname", value: formValues.firstname },
          { name: "Name", value: formValues.lastname },
          { name: "Email", value: formValues.email },
        ],
        formValues?.participantData,
        deliveryMode,
      );

      setProgress(100);
      if (deliveryMode === "email") {
        setFormValues({});
      }
      alert.show(t("registration_successful"), { type: "success" });
      return true;
    } catch (error) {
      setFormValues({ ...formValues, participantData: error?.participantData });
      throw error;
    }
  };

  const createParticipantAndDeliverTicket = async (
    tickets,
    participant,
    existingParticipantData,
    deliveryMode = "email",
  ) => {
    let participantData = existingParticipantData;

    if (!participantData) {
      participantData = await createPartnerParticipant({
        eventId,
        partnerId: props.partnerId,
        tickets,
        details: participant,
        subdivision: props.event?.subdivision,
      });
    }

    try {
      if (deliveryMode === "download") {
        await downloadPDF({
          participant: participantData,
          event: props.event,
        });
        return;
      }

      await sendEmail({
        participant: participantData,
        event: props.event,
        message: message,
        isPartner: true,
      });
    } catch (error) {
      console.error(error);
      const deliveryError = new Error(
        deliveryMode === "download" ? "Ticket download failed" : "Ticket email failed",
      );
      deliveryError.participantData = participantData;
      throw deliveryError;
    }
  };

  const updateTicketQuantity = (newQuantity, ticket) => {
    const baseVariantKey = buildVariantKey(ticket.id, []);
    const ticketIndex = selectedTickets.findIndex((item) => item.variantKey === baseVariantKey);
    let newArray = [...selectedTickets];

    if (newQuantity === 0) {
      if (ticketIndex >= 0) {
        newArray.splice(ticketIndex, 1);
      }
    } else {
      if (currentPromoCode.maxUsage !== 0) {
        let totalAfterUpdate = totalTickets(selectedTickets);

        if (ticketIndex === -1) {
          totalAfterUpdate += newQuantity;
        } else {
          totalAfterUpdate = totalAfterUpdate - selectedTickets[ticketIndex].number + newQuantity;
        }

        if (totalAfterUpdate + usedPartnerQuota > currentPromoCode.maxUsage) {
          alert.show(t("max_tickets_reached", { count: currentPromoCode.maxUsage }), {
            type: "info",
          });

          return;
        }
      }


      if (newQuantity > getTicketAvailableQuantity(ticket)) {
        alert.show(`${ticket.name} ${t("tickets_left", { count: getTicketAvailableQuantity(ticket), ns: "ticketitem" })}`, {
          type: "info",
        });
        return;
      }

      const ticketData = {
        ticketId: ticket.id,
        variantKey: baseVariantKey,
        name: ticket.name,
        number: newQuantity,
        sum: 0,
        description: ticket.description,
        optionSelections: [],
        optionPriceDelta: 0,
      };

      if (ticketIndex >= 0) {
        newArray[ticketIndex] = ticketData;
      } else {
        newArray.push(ticketData);
      }
    }

    setSelectedTickets(newArray);
  };

  const buildDefaultOptionSelections = (ticket) => {
    const defaultSelections = {};
    (ticket.options || []).forEach((group) => {
      if (group.selectionType === "single" && group.required && group.values?.length > 0) {
        defaultSelections[group.id] = [group.values[0].id];
      } else {
        defaultSelections[group.id] = [];
      }
    });
    return defaultSelections;
  };

  const openOptionsDropdown = (ticket) => {
    setOpenOptionsTicketId((prev) => (prev === ticket.id ? null : ticket.id));
    setOptionDraftByTicket((prev) => {
      if (prev[ticket.id]) return prev;
      return {
        ...prev,
        [ticket.id]: {
          selections: buildDefaultOptionSelections(ticket),
          quantity: 1,
        },
      };
    });
  };

  const buildVariantSelectionsPayload = (ticket, optionSelectionsState) => {
    const optionSelections = [];
    for (const group of ticket.options || []) {
      const selectedValueIds = optionSelectionsState[group.id] || [];
      if (group.required && selectedValueIds.length === 0) {
        return { error: "required" };
      }

      for (const selectedValueId of selectedValueIds) {
        const selectedValue = (group.values || []).find((value) => value.id === selectedValueId);
        if (!selectedValue) continue;

        optionSelections.push({
          groupId: group.id,
          groupName: group.name,
          valueId: selectedValue.id,
          valueLabel: selectedValue.label,
          priceDelta: Number(selectedValue.priceDelta || 0),
        });
      }
    }
    return { optionSelections };
  };

  const getSelectionsForOptionValue = (
    ticket,
    targetGroupId,
    targetValueId,
    { removeFromMulti = false } = {},
  ) => {
    const draftSelections =
      optionDraftByTicket[ticket.id]?.selections || buildDefaultOptionSelections(ticket);
    const nextSelections = { ...draftSelections };

    for (const group of ticket.options || []) {
      const current = nextSelections[group.id] || [];
      if (group.id === targetGroupId) {
        if (group.selectionType === "multi") {
          const alreadySelected = current.includes(targetValueId);
          nextSelections[group.id] = removeFromMulti
            ? current.filter((valueId) => valueId !== targetValueId)
            : alreadySelected
              ? current
              : [...current, targetValueId];
        } else {
          nextSelections[group.id] = [targetValueId];
        }
      } else if (group.selectionType === "single" && group.required && current.length === 0) {
        nextSelections[group.id] = group.values?.length ? [group.values[0].id] : [];
      } else {
        nextSelections[group.id] = current;
      }
    }

    return nextSelections;
  };

  const getVariantQuantityFromSelections = (ticket, optionSelectionsState) => {
    const { optionSelections } = buildVariantSelectionsPayload(ticket, optionSelectionsState);
    if (!optionSelections) return 0;
    const variantKey = buildVariantKey(ticket.id, optionSelections);
    const line = selectedTickets.find((item) => item.variantKey === variantKey);
    return Number(line?.number || 0);
  };

  const addOptionVariantToCartInline = (
    ticket,
    overrideSelections = null,
    overrideQuantity = null,
    replaceVariantKey = null,
  ) => {
    if (!ticket) return;
    const draft = optionDraftByTicket[ticket.id] || {
      selections: buildDefaultOptionSelections(ticket),
      quantity: 1,
    };
    const optionSelectionsState = overrideSelections || draft.selections || {};
    const optionQuantity = Math.max(1, Number(overrideQuantity ?? draft.quantity ?? 1));


    const { optionSelections, error } = buildVariantSelectionsPayload(ticket, optionSelectionsState);
    if (error === "required") {
      alert.show(t("requiredOptionMissing"), { type: "error" });
      return;
    }

    const variantKey = buildVariantKey(ticket.id, optionSelections);
    const cartLines = replaceVariantKey
      ? selectedTickets.filter((line) => line.variantKey !== replaceVariantKey)
      : selectedTickets;
    const inCartForTicket = cartLines
      .filter((line) => line.ticketId === ticket.id)
      .reduce((sum, line) => sum + Number(line.number || 0), 0);
    const existingIndex = cartLines.findIndex((line) => line.variantKey === variantKey);
    const existingQuantity = existingIndex >= 0 ? Number(cartLines[existingIndex].number || 0) : 0;

    for (const selection of optionSelections) {
      const remainingStock = getRemainingOptionStock(
        ticket,
        selection.groupId,
        selection.valueId,
        cartLines,
      );
      if (remainingStock !== null && remainingStock + existingQuantity < optionQuantity) {
        alert.show(t("optionOutOfStock", { value: selection.valueLabel }), { type: "error" });
        return;
      }
    }

    if (optionQuantity + inCartForTicket - existingQuantity > getTicketAvailableQuantity(ticket)) {
      alert.show(`${ticket.name} ${t("tickets_left", { count: ticket.numAvailable, ns: "ticketitem" })}`, {
        type: "info",
      });
      return;
    }

    if (currentPromoCode.maxUsage !== 0) {
      const currentTotal = totalTickets(cartLines);
      const nextTotal = currentTotal - existingQuantity + optionQuantity;
      if (nextTotal + usedPartnerQuota > currentPromoCode.maxUsage) {
        alert.show(t("max_tickets_reached", { count: currentPromoCode.maxUsage }), {
          type: "info",
        });
        return;
      }
    }

    const optionLabel = buildOptionSelectionLabel(optionSelections);
    const optionPriceDelta = getOptionPriceDelta(optionSelections);
    const lineName = optionLabel ? `${ticket.name} (${optionLabel})` : ticket.name;
    const nextLine = {
      ticketId: ticket.id,
      baseTicketId: ticket.id,
      variantKey,
      name: lineName,
      number: optionQuantity,
      basePrice: Number(ticket.price || 0),
      optionPriceDelta: Number(optionPriceDelta || 0),
      optionSelections,
      unitPrice: 0,
      sum: 0,
      description: ticket.description,
    };

    const nextSelectedTickets = [...cartLines];
    if (existingIndex >= 0) {
      nextSelectedTickets[existingIndex] = nextLine;
    } else {
      nextSelectedTickets.push(nextLine);
    }
    setSelectedTickets(nextSelectedTickets);
  };

  const setOptionValueQuantity = (ticket, group, value, nextQuantity) => {
    const safeQuantity = Math.max(0, Number(nextQuantity || 0));
    const currentSelections =
      optionDraftByTicket[ticket.id]?.selections || buildDefaultOptionSelections(ticket);
    const selectedInGroup = currentSelections[group.id] || [];
    const maxSelect = Number(group.maxSelect || 0);
    if (
      safeQuantity > 0 &&
      group.selectionType === "multi" &&
      !selectedInGroup.includes(value.id) &&
      maxSelect > 0 &&
      selectedInGroup.length >= maxSelect
    ) {
      alert.show(t("invalidTicketOptions"), { type: "error" });
      return;
    }

    const nextSelections = getSelectionsForOptionValue(ticket, group.id, value.id, {
      removeFromMulti: group.selectionType === "multi" && safeQuantity === 0,
    });
    const { optionSelections } = buildVariantSelectionsPayload(ticket, nextSelections);
    if (!optionSelections) return;

    const currentVariant = buildVariantSelectionsPayload(ticket, currentSelections).optionSelections;
    const currentVariantKey = currentVariant
      ? buildVariantKey(ticket.id, currentVariant)
      : null;
    const variantKey = buildVariantKey(ticket.id, optionSelections);
    if (safeQuantity === 0) {
      setOptionDraftByTicket((prev) => ({
        ...prev,
        [ticket.id]: {
          ...(prev[ticket.id] || {}),
          selections: nextSelections,
          quantity: 0,
        },
      }));
      setSelectedTickets(
        selectedTickets.filter((line) => line.variantKey !== (currentVariantKey || variantKey)),
      );
      return;
    }

    const replacesMultiVariant =
      group.selectionType === "multi" && !selectedInGroup.includes(value.id) && currentVariantKey;
    const currentVariantQuantity = currentVariantKey
      ? Number(selectedTickets.find((line) => line.variantKey === currentVariantKey)?.number || 0)
      : 0;
    const effectiveQuantity = replacesMultiVariant
      ? Math.max(safeQuantity, currentVariantQuantity)
      : safeQuantity;

    setOptionDraftByTicket((prev) => ({
      ...prev,
      [ticket.id]: {
        ...(prev[ticket.id] || {}),
        selections: nextSelections,
        quantity: effectiveQuantity,
      },
    }));

    addOptionVariantToCartInline(
      ticket,
      nextSelections,
      effectiveQuantity,
      replacesMultiVariant ? currentVariantKey : null,
    );
  };

  const getTotalQuantityForTicket = (ticketId) =>
    selectedTickets
      .filter((ticket) => ticket.ticketId === ticketId)
      .reduce((total, ticket) => total + Number(ticket.number || 0), 0);

  const decrement = (ticket) => {
    const baseVariantKey = buildVariantKey(ticket.id, []);
    const currentTicket = selectedTickets.find((item) => item.variantKey === baseVariantKey);
    const currentQuantity = currentTicket?.number || 0;
    if (currentQuantity > 0) updateTicketQuantity(currentQuantity - 1, ticket);
  };

  const increment = (ticket) => {
    const baseVariantKey = buildVariantKey(ticket.id, []);
    const currentTicket = selectedTickets.find((item) => item.variantKey === baseVariantKey);
    const currentQuantity = currentTicket?.number || 0;
    updateTicketQuantity(currentQuantity + 1, ticket);
  };

  const updateQuantity = (event, ticket) => {
    const newQuantity = parseInt(event.target.value) || 0;
    if (newQuantity >= 0) updateTicketQuantity(newQuantity, ticket);
  };

  const getTicketById = (ticketId) => tickets.find((ticket) => ticket.id === ticketId);

  const updateCartLineQuantity = (variantKey, nextQuantity) => {
    const line = selectedTickets.find((item) => item.variantKey === variantKey);
    if (!line) return;

    const ticket = getTicketById(line.ticketId);
    if (!ticket) return;

    if (nextQuantity <= 0) {
      setSelectedTickets(selectedTickets.filter((item) => item.variantKey !== variantKey));
      return;
    }


    const otherLinesQuantity = selectedTickets
      .filter((item) => item.ticketId === line.ticketId && item.variantKey !== variantKey)
      .reduce((sum, item) => sum + Number(item.number || 0), 0);

    if (otherLinesQuantity + nextQuantity > getTicketAvailableQuantity(ticket)) {
      alert.show(`${ticket.name} ${t("tickets_left", { count: ticket.numAvailable, ns: "ticketitem" })}`, {
        type: "info",
      });
      return;
    }

    if (Array.isArray(line.optionSelections)) {
      for (const selection of line.optionSelections) {
        const remainingStock = getRemainingOptionStock(
          ticket,
          selection.groupId,
          selection.valueId,
          selectedTickets,
        );
        if (remainingStock !== null && remainingStock + Number(line.number || 0) - nextQuantity < 0) {
          alert.show(t("optionOutOfStock", { value: selection.valueLabel }), { type: "error" });
          return;
        }
      }
    }

    if (currentPromoCode.maxUsage !== 0) {
      const currentTotal = totalTickets(selectedTickets);
      const nextTotal = currentTotal - Number(line.number || 0) + nextQuantity;
      if (nextTotal + usedPartnerQuota > currentPromoCode.maxUsage) {
        alert.show(t("max_tickets_reached", { count: currentPromoCode.maxUsage }), {
          type: "info",
        });
        return;
      }
    }

    const nextTickets = selectedTickets.map((item) =>
      item.variantKey === variantKey
        ? {
            ...item,
            number: nextQuantity,
          }
        : item,
    );
    setSelectedTickets(nextTickets);
  };

  return (
    <section className="__event-desc partner-event-page">
      <div className="container">
        <div className="__section-header">
          <h2 className="__section-header-title">{props.event.name ?? ""}</h2>
        </div>

        <ModalComponent
          isOpen={failModalOpen}
          onClose={() => setFailModalOpen(false)}
          title={t("invitation_error")}
          message={[t("invitation_info"), failureMessage].filter(Boolean).join(" ")}
        />

        {Number(currentPromoCode?.currentUsage || 0) >= currentPromoCode?.maxUsage &&
          currentPromoCode?.maxUsage !== 0 && (
            <div className="__section-header">
              <h2 className="__section-header-title">{t("all_promo_codes_used")}</h2>
            </div>
          )}

        {currentPromoCode?.isPartnerCode && (
            <div className="__section-header-buttons partner-entry-mode-tabs">
              <button
                type="button"
                aria-pressed={!showTicketHistory && !isCsvCreation}
                className={!showTicketHistory && !isCsvCreation ? "__active" : ""}
                onClick={() => { setIsCsvCreation(false); setShowTicketHistory(false); }}
                disabled={isLoading || !showForm}
              >
                {t("add_one_by_one")}
              </button>
              <button
                type="button"
                aria-pressed={!showTicketHistory && isCsvCreation}
                className={!showTicketHistory && isCsvCreation ? "__active" : ""}
                onClick={() => {
                  setIsCsvCreation(true);
                  setShowTicketHistory(false);
                }}
                disabled={isLoading || !showForm}
              >
                {t("add_multiple")}
              </button>
              <button type="button" aria-pressed={showTicketHistory} className={showTicketHistory ? "__active" : ""} disabled={isLoading}
                onClick={() => setShowTicketHistory(true)}>{t("partner_your_tickets")}</button>
            </div>
        )}
        {!showForm && Number(currentPromoCode?.pendingPartnerUsage || 0) > 0 && <p role="status">{t("partner_quota_pending")}</p>}
        {showTicketHistory && <PartnerTickets event={props.event} partnerId={props.partnerId} />}

        {showForm && !showTicketHistory && (
          <>

            {!isCsvCreation ? (
              <>
                <div className="__details-wrapper">
                  <form className="__details-wrapper-left" onSubmit={checkoutHandler}>
                    <h3 className="__details-form-title">{t("fill_the_form")}</h3>
                    <div className="row g-2 g-lg-3">
                      <div className="col-6">
                        <Input
                          placeholder={t("add_firstname")}
                          label={t("firstname")}
                          type="text"
                          id="Firstname"
                          onChange={(e) => inputChangeHandler("firstname", e.target.value)}
                          required="required"
                          value={formValues.firstname || ""}
                        />
                      </div>
                      <div className="col-6">
                        <Input
                          placeholder={t("add_lastname")}
                          label={t("lastname")}
                          type="text"
                          id="Name"
                          onChange={(e) => inputChangeHandler("lastname", e.target.value)}
                          required="required"
                          value={formValues.lastname || ""}
                        />
                      </div>
                      <div className="col-12">
                        <Input
                          placeholder={t("email_placeholder")}
                          label={t("email")}
                          type="text"
                          id="Email"
                          onChange={(e) => inputChangeHandler("email", e.target.value)}
                          required="required"
                          value={formValues.email || ""}
                        />
                        <div className="text-sm mt-2">
                          <span className="me-1">
                            <Asterisk />
                          </span>
                          {t("valid_email_warning")}
                        </div>
                      </div>
                    </div>
                  </form>
                  <div className="__details-wrapper-right">
                    {loadingTickets ? (
                      <LoadingSpinner height="60%" width="60%" />
                    ) : tickets.length ? (
                      <>
                        <div className="__ticket-wrapper">
                          <h2 className="__ticket-wrapper-title">{t("choose_your_tickets")}</h2>
                          <p className="remaining-tickets">
                            {t("remaining_tickets", {
                              remaining:
                                currentPromoCode?.maxUsage === 0
                                  ? t("unlimited")
                                  : (currentPromoCode?.maxUsage || 0) -
                                    (usedPartnerQuota || 0),
                            })}
                          </p>
                          {tickets.map((ticket) => (
                            <div key={`${ticket.id}-${ticket.price}`}>
                              <TicketItem
                                ticket={{ ...ticket, promoPrice: getPartnerLinePrice(ticket, { number: 1 }, currentPromoCode).unitPrice }}
                                selectedTickets={selectedTickets}
                                increment={() => increment(ticket)}
                                decrement={() => decrement(ticket)}
                                updateQuantity={(e) => updateQuantity(e, ticket)}
                                onTicketClick={() => increment(ticket)}
                                quantityInCart={getTotalQuantityForTicket(ticket.id)}
                                availableQuantity={getTicketAvailableQuantity(ticket)}
                                showPromoPrice={true}
                                hasOptions={Array.isArray(ticket.options) && ticket.options.length > 0}
                                onToggleOptions={() => openOptionsDropdown(ticket)}
                                isOptionsOpen={openOptionsTicketId === ticket.id}
                              />
                              {Array.isArray(ticket.options) &&
                                ticket.options.length > 0 &&
                                openOptionsTicketId === ticket.id && (
                                  <div className="ticket-options-dropdown">
                                    {(ticket.options || []).map((group) => (
                                      <div key={group.id} className="ticket-options-group">
                                        <label>
                                          {group.name}
                                          {group.required ? " *" : ""}
                                        </label>
                                        <div className="ticket-options-values">
                                          {(group.values || []).map((value) => {
                                            const remainingStock = getRemainingOptionStock(
                                              ticket,
                                              group.id,
                                              value.id,
                                              selectedTickets,
                                            );
                                            const rowSelections = getSelectionsForOptionValue(
                                              ticket,
                                              group.id,
                                              value.id,
                                            );
                                            const quantity = getVariantQuantityFromSelections(
                                              ticket,
                                              rowSelections,
                                            );
                                            const isOutOfStock =
                                              remainingStock !== null && remainingStock <= 0 && quantity === 0;
                                            return (
                                              <div
                                                key={value.id}
                                                className={`__ticket-item ticket-option-ticket-item ${
                                                  isOutOfStock ? "is-out-of-stock" : ""
                                                }`}
                                                onClick={() => {
                                                  if (isOutOfStock) return;
                                                  setOptionValueQuantity(ticket, group, value, quantity + 1);
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(event) => {
                                                  if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    if (isOutOfStock) return;
                                                    setOptionValueQuantity(ticket, group, value, quantity + 1);
                                                  }
                                                }}
                                              >
                                                <div className="__ticket-item-icon">
                                                  {ticket.image ? (
                                                    <img
                                                      src={ticket.image}
                                                      alt={value.label}
                                                      className="ticket-icon-image"
                                                    />
                                                  ) : (
                                                    <svg
                                                      viewBox="0 0 40 41"
                                                      fill="none"
                                                      xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                      <g clipPath={`url(#clip-partner-ticket-option-${ticket.id}-${value.id})`}>
                                                        <path
                                                          d="M35.0002 5.875C35.4422 5.875 35.8661 6.05059 36.1787 6.36316C36.4912 6.67572 36.6668 7.09964 36.6668 7.54167V16.7083C35.5618 16.7083 34.502 17.1473 33.7206 17.9287C32.9392 18.7101 32.5002 19.7699 32.5002 20.875C32.5002 21.9801 32.9392 23.0399 33.7206 23.8213C34.502 24.6027 35.5618 25.0417 36.6668 25.0417V34.2083C36.6668 34.6504 36.4912 35.0743 36.1787 35.3868C35.8661 35.6994 35.4422 35.875 35.0002 35.875H5.00016C4.55814 35.875 4.13421 35.6994 3.82165 35.3868C3.50909 35.0743 3.3335 34.6504 3.3335 34.2083V25.0417C4.43856 25.0417 5.49837 24.6027 6.27977 23.8213C7.06118 23.0399 7.50016 21.9801 7.50016 20.875C7.50016 19.7699 7.06118 18.7101 6.27977 17.9287C5.49837 17.1473 4.43856 16.7083 3.3335 16.7083V7.54167C3.3335 7.09964 3.50909 6.67572 3.82165 6.36316C4.13421 6.05059 4.55814 5.875 5.00016 5.875H35.0002ZM33.3335 9.20833H6.66683V14.155L6.92683 14.29C8.05736 14.9067 9.01004 15.8042 9.69311 16.8959C10.3762 17.9877 10.7666 19.2369 10.8268 20.5233L10.8335 20.875C10.8337 22.2221 10.471 23.5443 9.78358 24.7028C9.09614 25.8612 8.10931 26.8131 6.92683 27.4583L6.66683 27.595V32.5417H33.3335V27.5933L33.0735 27.46C31.943 26.8433 30.9903 25.9458 30.3072 24.8541C29.6242 23.7623 29.2337 22.5131 29.1735 21.2267L29.1668 20.875C29.1668 18.035 30.7452 15.5633 33.0735 14.2917L33.3335 14.1533V9.20833ZM26.6668 15.875V25.875H13.3335V15.875H26.6668Z"
                                                          fill="#8BA4BF"
                                                        />
                                                      </g>
                                                      <defs>
                                                        <clipPath id={`clip-partner-ticket-option-${ticket.id}-${value.id}`}>
                                                          <rect
                                                            width="40"
                                                            height="40"
                                                            fill="white"
                                                            transform="translate(0 0.875)"
                                                          />
                                                        </clipPath>
                                                      </defs>
                                                    </svg>
                                                  )}
                                                </div>
                                                <div className="__ticket-item-cont">
                                                  <div>
                                                    <span className="info">
                                                      {quantity > 0
                                                        ? t("inCartCount", { count: quantity, ns: "ticketitem" })
                                                        : remainingStock !== null
                                                          ? t("optionStockRemainingShort", { count: remainingStock })
                                                          : t("unlimited")}
                                                    </span>
                                                    <h6 className="subtitle">{value.label}</h6>
                                                    <span className="price">{getPartnerLinePrice(ticket, { number: 1, optionSelections: [{ groupId: group.id, valueId: value.id }] }, currentPromoCode).unitPrice.toFixed(2)} €</span>
                                                  </div>
                                                  <div className="right-detail">
                                                    <input
                                                      type="number"
                                                      min="0"
                                                      className="quantity"
                                                      value={quantity}
                                                      onChange={(event) =>
                                                        setOptionValueQuantity(
                                                          ticket,
                                                          group,
                                                          value,
                                                          parseInt(event.target.value, 10) || 0,
                                                        )
                                                      }
                                                      onClick={(event) => event.stopPropagation()}
                                                      disabled={isOutOfStock}
                                                    />
                                                    <div className="qty-btns">
                                                      <div
                                                        className="qty-btn"
                                                        onClick={(event) => {
                                                          event.stopPropagation();
                                                          setOptionValueQuantity(ticket, group, value, quantity + 1);
                                                        }}
                                                      >
                                                        <svg
                                                          className="qty-btn-icon"
                                                          width="26"
                                                          height="23"
                                                          viewBox="0 0 26 23"
                                                          fill="none"
                                                          xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                          <path d="M11.935 10.3828V4.80469H14.0647V10.3828H20.4536V12.2422H14.0647V17.8203H11.935V12.2422H5.54614V10.3828H11.935Z" />
                                                        </svg>
                                                      </div>
                                                      <div
                                                        className="qty-btn"
                                                        onClick={(event) => {
                                                          event.stopPropagation();
                                                          setOptionValueQuantity(
                                                            ticket,
                                                            group,
                                                            value,
                                                            Math.max(0, quantity - 1),
                                                          );
                                                        }}
                                                      >
                                                        <svg
                                                          className="qty-btn-icon"
                                                          width="26"
                                                          height="24"
                                                          viewBox="0 0 26 24"
                                                          fill="none"
                                                          xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                          <path d="M11.935 11.0078H14.0647H20.4536V12.8672H14.0647H11.935H5.54614V11.0078H11.935Z" />
                                                        </svg>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                        <div className="__ticket-checkout">
                          {selectedTickets.length > 0 && (
                            <div className="ticket-cart-review">
                              <h4>{t("cartReview")}</h4>
                              {pricedTickets.map((line) => (
                                <div
                                  className="ticket-cart-row"
                                  key={line.variantKey || `${line.ticketId}-${line.name}`}
                                >
                                  <div className="ticket-cart-row-main">
                                    <div className="ticket-cart-row-name">{line.name}</div>
                                    <div className="ticket-cart-row-price">
                                      {Number(line.sum || 0).toFixed(2)} €
                                    </div>
                                  </div>
                                  <div className="ticket-cart-row-actions">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateCartLineQuantity(
                                          line.variantKey,
                                          Number(line.number || 0) - 1,
                                        )
                                      }
                                    >
                                      -
                                    </button>
                                    <span>{line.number}</span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        updateCartLineQuantity(
                                          line.variantKey,
                                          Number(line.number || 0) + 1,
                                        )
                                      }
                                    >
                                      +
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => updateCartLineQuantity(line.variantKey, 0)}
                                    >
                                      {t("remove")}
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="__ticket-checkout-titles">
                            <div className="subtitle">{t("total_amount")}</div>
                            <h4 className="total-price">{totalAmount.toFixed(2)} €</h4>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p>{t("no_tickets_available")}</p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <CsvForm
                tickets={tickets}
                setParticipants={setParticipants}
                participants={participants}
                participantPrice={participantPrice}
              />
            )}

            {isCsvCreation && (
              <div className="partner-payment-panel">
                <div className="__ticket-checkout-titles"><span>{t("total_amount")}</span><strong>{totalAmount.toFixed(2)} €</strong></div>
                {totalAmount > 0 && <fieldset>
                  <legend>{t("partner_payer")}</legend>
                  <div className="row g-3">
                    {[ ["firstname", "firstname", "given-name"], ["lastname", "lastname", "family-name"], ["email", "email", "email"] ].map(([field, label, autocomplete]) => (
                      <div className={field === "email" ? "col-12" : "col-12 col-sm-6"} key={field}>
                        <Input id={`partner-payer-${field}`} label={t(label)} type={field === "email" ? "email" : "text"} autoComplete={autocomplete} required value={payer[field] || ""} onChange={(event) => setPayer((current) => ({ ...current, [field]: event.target.value }))} />
                      </div>
                    ))}
                  </div>
                </fieldset>}
              </div>
            )}

            <div className="__section-footer partner-email-message">
              <div className="__section-footer-description">
                <label className="__section-footer-description-label">{t("email_subject")}</label>
                <textarea
                  className="__form-control"
                  placeholder={t("email_message_placeholder")}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <p className="__section-footer-description-warning">{t("email_warning")}</p>
              </div>
            </div>

            {totalAmount > 0 && <p className="partner-availability-note">{t("partner_no_payment_hold")}</p>}
            <div className="__details-wrapper-btn">
              {isLoading ? (
                <ProgressBar title={t("processing_tickets", { progress })} progress={progress} />
              ) : (
                <div className="partner-ticket-actions">
                  {totalAmount > 0 ? <>
                    <button type="button" className="checkoutBtn partner-pay-bancontact" onClick={(event) => checkoutHandler(event, "bancontact")}>{t("pay_with_bancontact")}</button>
                    <button type="button" className="checkoutBtn partner-pay-card" onClick={(event) => checkoutHandler(event, "card")}>{t("pay_with_mastercard_visa")}</button>
                  </> : <>
                  <button
                    type="submit"
                    className="checkoutBtn"
                    onClick={(event) => checkoutHandler(event, "email")}
                  >
                    {t("send_by_email")}
                  </button>
                  <button
                    type="button"
                    className="checkoutBtn partner-download-btn"
                    onClick={(event) => checkoutHandler(event, "download")}
                  >
                    {t("download_tickets_here")}
                  </button>
                  </>}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default EventDescPartner;
